import { dashboardReference, upsertReference } from '@/lib/dashboardReferences';
import { type DashboardSummary, getDashboard, listDashboards } from '@/lib/dashboards-api';
import { getFileExtension } from '@/lib/fileType';
import { cn } from '@/lib/utils';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import { toast } from 'sonner';

import {
    ICommand,
    commandsState,
    sideViewState,
    useConfig
} from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { DASHBOARD_LIST_KEY } from '@/components/Dashboards/useDashboardTitles';
import { openReferencedFile } from '@/components/chat/FileReferenceChip';
import { FluidHoverHighlight } from '@/components/fluid-hover-highlight';
import Icon from '@/components/Icon';
import { OfficeFileIcon } from '@/components/icons/OfficeFileIcon';
import {
    Command,
    CommandGroup,
    CommandItemAnimated,
    CommandListScrollable
} from '@/components/ui/command';
import {
    Popover,
    PopoverAnchor,
    PopoverContent
} from '@/components/ui/popover';

import { menuItemClassName } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

import type { ItemRect } from '@/hooks/use-fluid-hover';
import { useCommandNavigation } from '@/hooks/useCommandNavigation';
import { SessionFile, useSessionFiles } from '@/hooks/useSessionFiles';

import { dashboardReferencesState, debugModeState } from '@/state/chat';

import {
    AgentDebugView,
    LOCAL_COMMANDS,
    findLocalCommand,
    isLocalCommand
} from './localCommands';

import DashboardPreview from './DashboardPreview';

import FileReferenceHighlight, {
    hasFileReference
} from './FileReferenceHighlight';

const FILE_REFERENCE_PATTERN = /(^|\s)#([^\s#]*)$/;
const PREVIEW_DELAY_MS = 350;
const DASHBOARD_COMMAND_PATTERN = /^\/dashboard (.*)$/;

const DASHBOARD_COMMAND: ICommand = {
    id: '/dashboard',
    icon: 'layout-dashboard',
    description: 'Reference one of your dashboards.'
};

type ReferenceItem =
    | { type: 'dashboard'; dashboard: DashboardSummary }
    | { type: 'file'; file: SessionFile };

interface Props {
    id?: string;
    className?: string;
    autoFocus?: boolean;
    placeholder?: string;
    selectedCommand?: ICommand;
    setSelectedCommand: (command: ICommand | undefined) => void;
    onChange: (value: string) => void;
    onPaste?: (event: any) => void;
    onEnter?: () => void;
    onLocalCommand?: (view: AgentDebugView) => void;
}

export interface InputMethods {
    reset: () => void;
    focus: () => void;
    setValue: (value: string) => void;
}

const Input = forwardRef<InputMethods, Props>(
    (
        {
            placeholder,
            id,
            className,
            autoFocus,
            selectedCommand,
            setSelectedCommand,
            onChange,
            onEnter,
            onPaste,
            onLocalCommand
        },
        ref
    ) => {
        const commands = useRecoilValue(commandsState);
        const { config } = useConfig();
        const artifactsEnabled = config?.features?.artifacts_enabled === true;
        const debugMode = useRecoilValue(debugModeState);
        const [isComposing, setIsComposing] = useState(false);
        const [showCommands, setShowCommands] = useState(false);
        const [commandInput, setCommandInput] = useState('');
        const [value, setValue] = useState('');
        const textareaRef = useRef<HTMLTextAreaElement>(null);

        const normalizedInput = commandInput.toLowerCase().slice(1);

        const dashboardOnly =
            artifactsEnabled && DASHBOARD_COMMAND_PATTERN.test(value);

        const availableCommands: ICommand[] = [
            ...(artifactsEnabled ? [DASHBOARD_COMMAND] : []),
            ...(debugMode ? [...LOCAL_COMMANDS, ...commands] : commands)
        ];

        const filteredCommands = availableCommands
            .filter((command) =>
                command.id.toLowerCase().includes(normalizedInput)
            )
            .sort((a, b) => {
                const indexA = a.id.toLowerCase().indexOf(normalizedInput);
                const indexB = b.id.toLowerCase().indexOf(normalizedInput);
                return indexA - indexB;
            });

        const {
            selectedIndex,
            handleMouseMove,
            handleMouseLeave,
            handleKeyDown: navigationKeyDown
        } = useCommandNavigation({
            items: filteredCommands,
            isOpen: showCommands,
            onSelect: (command) => {
                handleCommandSelect(command);
            },
            onClose: () => {
                setShowCommands(false);
                setCommandInput('');
            }
        });

        const {
            files: sessionFiles,
            isLoading: isLoadingSessionFiles,
            refresh: refreshSessionFiles
        } = useSessionFiles();
        const [fileQuery, setFileQuery] = useState<string | null>(null);
        const [insertedFilenames, setInsertedFilenames] = useState<string[]>(
            []
        );
        const showFiles = fileQuery !== null;
        const [isOverReference, setIsOverReference] = useState(false);
        const containerRef = useRef<HTMLDivElement>(null);
        const threadElements = useRecoilValue(sideViewState)?.elements ?? [];

        const [fileListElement, setFileListElement] =
            useState<HTMLDivElement | null>(null);
        const [highlightRect, setHighlightRect] = useState<ItemRect | null>(
            null
        );

        const setDashboardReferences = useSetRecoilState(
            dashboardReferencesState
        );
        const { data: dashboards = [], isLoading: isLoadingDashboards } =
            useSWR(showFiles && dashboardOnly ? DASHBOARD_LIST_KEY : null, listDashboards, {
                revalidateOnFocus: false
            });

        const filteredFiles =
            fileQuery === null
                ? []
                : sessionFiles.filter((file) =>
                      file.filename
                          .toLowerCase()
                          .includes(fileQuery.toLowerCase())
                  );

        const filteredDashboards =
            fileQuery === null
                ? []
                : dashboards.filter((dashboard) =>
                      dashboard.title
                          .toLowerCase()
                          .includes(fileQuery.toLowerCase())
                  );

        const referenceItems: ReferenceItem[] = dashboardOnly
            ? filteredDashboards.map(
                  (dashboard): ReferenceItem => ({ type: 'dashboard', dashboard })
              )
            : filteredFiles.map((file): ReferenceItem => ({ type: 'file', file }));

        const {
            selectedIndex: selectedFileIndex,
            handleMouseMove: handleFileMouseMove,
            handleMouseLeave: handleFileMouseLeave,
            handleKeyDown: fileNavigationKeyDown
        } = useCommandNavigation({
            items: referenceItems,
            isOpen: showFiles,
            onSelect: (item: ReferenceItem) => {
                handleReferenceSelect(item);
            },
            onClose: () => closeReferenceMenu()
        });

        useEffect(() => {
            fileListElement
                ?.querySelector(`[data-index="${selectedFileIndex}"]`)
                ?.scrollIntoView({ block: 'nearest' });
        }, [fileListElement, selectedFileIndex]);

        useLayoutEffect(() => {
            const row = fileListElement?.querySelector<HTMLElement>(
                `[data-index="${selectedFileIndex}"]`
            );
            setHighlightRect(
                row
                    ? {
                          top: row.offsetTop,
                          left: row.offsetLeft,
                          width: row.offsetWidth,
                          height: row.offsetHeight
                      }
                    : null
            );
        }, [fileListElement, selectedFileIndex, referenceItems.length]);

        const [previewDashboardId, setPreviewDashboardId] = useState<
            string | null
        >(null);
        const highlightedItem = referenceItems[selectedFileIndex];
        const highlightedDashboardId =
            showFiles && highlightedItem?.type === 'dashboard'
                ? highlightedItem.dashboard.id
                : null;

        useEffect(() => {
            setPreviewDashboardId(null);
            if (!highlightedDashboardId) return;
            const timer = setTimeout(
                () => setPreviewDashboardId(highlightedDashboardId),
                PREVIEW_DELAY_MS
            );
            return () => clearTimeout(timer);
        }, [highlightedDashboardId]);

        const reset = () => {
            setValue('');
            if (!selectedCommand?.persistent) {
                setSelectedCommand(undefined);
            }
            setCommandInput('');
            setShowCommands(false);
            setFileQuery(null);
            setInsertedFilenames([]);
            onChange('');
        };

        const focus = () => {
            textareaRef.current?.focus();
        };

        useImperativeHandle(ref, () => ({
            reset,
            focus,
            setValue: (newValue: string) => {
                setValue(newValue);
                onChange(newValue);
            }
        }));

        useEffect(() => {
            if (textareaRef.current && autoFocus) {
                textareaRef.current.focus();
            }
        }, [autoFocus]);

        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            setValue(newValue);
            onChange(newValue);

            const dashboardMatch = artifactsEnabled
                ? newValue.match(DASHBOARD_COMMAND_PATTERN)
                : null;
            if (dashboardMatch) {
                setShowCommands(false);
                setCommandInput('');
                setFileQuery(dashboardMatch[1]);
                return;
            }

            const words = newValue.split(' ');
            if (
                words.length === 1 &&
                words[0].startsWith('/') &&
                (debugMode || !findLocalCommand(words[0]))
            ) {
                setShowCommands(true);
                setCommandInput(words[0]);
            } else {
                setShowCommands(false);
                setCommandInput('');
            }

            const textBeforeCaret = newValue.slice(
                0,
                e.target.selectionStart ?? newValue.length
            );
            const fileMatch = textBeforeCaret.match(FILE_REFERENCE_PATTERN);
            if (fileMatch) {
                if (fileQuery === null) {
                    refreshSessionFiles();
                }
                setFileQuery(fileMatch[2]);
            } else {
                setFileQuery(null);
            }
        };

        const handleFileSelect = (file: SessionFile) => {
            const textarea = textareaRef.current;
            const caret = textarea?.selectionStart ?? value.length;
            const before = value
                .slice(0, caret)
                .replace(FILE_REFERENCE_PATTERN, `$1#${file.filename} `);
            const newValue = before + value.slice(caret);
            setValue(newValue);
            onChange(newValue);
            setFileQuery(null);
            setInsertedFilenames((names) =>
                names.includes(file.filename) ? names : [...names, file.filename]
            );

            setTimeout(() => {
                textarea?.focus();
                textarea?.setSelectionRange(before.length, before.length);
            }, 0);
        };

        const handleDashboardSelect = async (dashboard: DashboardSummary) => {
            const textarea = textareaRef.current;
            const caret = textarea?.selectionStart ?? value.length;
            const before = dashboardOnly
                ? ''
                : value.slice(0, caret).replace(FILE_REFERENCE_PATTERN, '$1');
            const newValue = dashboardOnly ? '' : before + value.slice(caret);
            setValue(newValue);
            onChange(newValue);
            setFileQuery(null);

            setTimeout(() => {
                textarea?.focus();
                textarea?.setSelectionRange(before.length, before.length);
            }, 0);

            try {
                const full = await getDashboard(dashboard.id);
                setDashboardReferences((current) =>
                    upsertReference(current, dashboardReference(full))
                );

            } catch (error) {
                toast.error(
                    error instanceof Error
                        ? `Could not add the dashboard: ${error.message}`
                        : 'Could not add the dashboard'
                );
                setValue(value);
                onChange(value);
            }
        };

        const handleReferenceSelect = (item: ReferenceItem) => {
            if (item.type === 'dashboard') {
                handleDashboardSelect(item.dashboard);
            } else {
                handleFileSelect(item.file);
            }
        };

        const handleCommandSelect = (command: ICommand) => {
            setShowCommands(false);
            if (command.id === DASHBOARD_COMMAND.id) {
                setValue('/dashboard ');
                onChange('/dashboard ');
                setCommandInput('');
                setFileQuery('');
                setTimeout(() => textareaRef.current?.focus(), 0);
                return;
            }
            if (isLocalCommand(command)) {
                setValue('');
                onChange('');
                setCommandInput('');
                onLocalCommand?.(command.view);
                return;
            }
            setSelectedCommand(command);

            // Remove the command text from the input
            const newValue = value.replace(commandInput, '').trimStart();
            setValue(newValue);
            onChange(newValue);

            setCommandInput('');

            // Focus back on textarea
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        };

        const closeReferenceMenu = () => {
            setFileQuery(null);
            if (dashboardOnly) {
                setValue('');
                onChange('');
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (dashboardOnly && referenceItems.length === 0) {
                if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    closeReferenceMenu();
                    return;
                }
            }

            // Handle command selection - check this FIRST before other key handling
            if (showCommands && filteredCommands.length > 0) {
                navigationKeyDown(e);
                // If the navigation handled the key, don't process further
                if (e.defaultPrevented) {
                    return;
                }
            }

            if (showFiles && referenceItems.length > 0) {
                fileNavigationKeyDown(e);
                if (e.defaultPrevented) {
                    return;
                }
            }

            // Handle regular enter only if command menu is not showing
            if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                onEnter &&
                !isComposing &&
                !showCommands
            ) {
                e.preventDefault();
                onEnter();
            }
        };

        const referencedFilenames = Array.from(
            new Set([
                ...insertedFilenames,
                ...sessionFiles.map((file) => file.filename)
            ])
        );
        const highlightReferences = hasFileReference(value, referencedFilenames);

        const referenceOpenerAt = (x: number, y: number) => {
            const reference = Array.from(
                containerRef.current?.querySelectorAll<HTMLElement>(
                    '[data-file-reference]'
                ) ?? []
            ).find((chip) =>
                Array.from(chip.getClientRects()).some(
                    (rect) =>
                        x >= rect.left &&
                        x <= rect.right &&
                        y >= rect.top &&
                        y <= rect.bottom
                )
            );
            return reference?.dataset.fileReference
                ? openReferencedFile(
                      threadElements,
                      reference.dataset.fileReference
                  )
                : undefined;
        };

        const handleTextareaMouseMove = (
            e: React.MouseEvent<HTMLTextAreaElement>
        ) => {
            setIsOverReference(
                highlightReferences &&
                    referenceOpenerAt(e.clientX, e.clientY) !== undefined
            );
        };

        const handleTextareaClick = (
            e: React.MouseEvent<HTMLTextAreaElement>
        ) => {
            const textarea = e.currentTarget;
            if (
                !highlightReferences ||
                textarea.selectionStart !== textarea.selectionEnd
            ) {
                return;
            }
            referenceOpenerAt(e.clientX, e.clientY)?.();
        };

        return (
            <div ref={containerRef} className="relative w-full">
                {highlightReferences && (
                    <FileReferenceHighlight
                        value={value}
                        filenames={referencedFilenames}
                        textareaRef={textareaRef}
                    />
                )}
                <AutoResizeTextarea
                    ref={textareaRef}
                    id={id}
                    autoFocus={autoFocus}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={onPaste}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    onMouseMove={handleTextareaMouseMove}
                    onMouseLeave={() => setIsOverReference(false)}
                    onClick={handleTextareaClick}
                    spellCheck={false}
                    placeholder={placeholder}
                    className={cn(
                        'bg-transparent focus:outline-hidden w-full placeholder:text-muted-foreground resize-none text-base leading-6',
                        highlightReferences &&
                            'relative text-transparent caret-foreground',
                        isOverReference && 'cursor-pointer',
                        className
                    )}
                    maxHeight={250}
                />

                <Popover open={showCommands && filteredCommands.length > 0}>
                    <PopoverAnchor className="top-0 left-0 absolute w-full h-0" />
                    <PopoverContent
                        side="top"
                        align="start"
                        sideOffset={16}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                        onMouseLeave={handleMouseLeave}
                        className="bg-transparent shadow-none p-0 border-0 w-auto max-w-[calc(100vw-2rem)]"
                    >
                        <Command className="bg-background shadow-md border rounded-lg">
                            <CommandListScrollable
                                maxItems={5}
                                className="custom-scrollbar"
                            >
                                <CommandGroup className="p-2">
                                    {filteredCommands.map((command, index) => (
                                        <CommandItemAnimated
                                            key={command.id}
                                            index={index}
                                            isSelected={index === selectedIndex}
                                            onMouseMove={() =>
                                                handleMouseMove(index)
                                            }
                                            onSelect={() =>
                                                handleCommandSelect(command)
                                            }
                                            className="space-x-2 command-item"
                                        >
                                            {command.id !==
                                                DASHBOARD_COMMAND.id && (
                                                <Icon
                                                    name={command.icon}
                                                    className={cn(
                                                        'size-5! text-muted-foreground transition-transform duration-150',
                                                        index === selectedIndex &&
                                                            'scale-110'
                                                    )}
                                                />
                                            )}
                                            <div className="flex-1">
                                                <div className="font-medium">
                                                    {command.id}
                                                </div>
                                                <div className="text-muted-foreground text-sm">
                                                    {command.description}
                                                </div>
                                            </div>
                                        </CommandItemAnimated>
                                    ))}
                                </CommandGroup>
                            </CommandListScrollable>
                        </Command>
                    </PopoverContent>
                </Popover>

                <Popover
                    open={showFiles}
                    onOpenChange={(open) => !open && closeReferenceMenu()}
                >
                    <PopoverAnchor className="top-0 left-0 absolute w-full h-0" />
                    <PopoverContent
                        side="top"
                        align="start"
                        sideOffset={16}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        onMouseLeave={handleFileMouseLeave}
                        className="bg-background p-0 rounded-xl w-96 max-w-[calc(100vw-2rem)]"
                    >
                        {dashboardOnly && (
                            <div className="px-4 pt-2 pb-0.5 text-(length:--fs-caption) text-muted-foreground leading-(--lh-caption)">
                                Dashboards
                            </div>
                        )}
                        {referenceItems.length > 0 ? (
                            <div
                                ref={setFileListElement}
                                className="relative p-1.5 max-h-60 overflow-y-auto custom-scrollbar"
                            >
                                <FluidHoverHighlight
                                    rect={highlightRect}
                                    session={0}
                                    className="rounded-lg"
                                />
                                {referenceItems.map((item, index) => (
                                    <div
                                        key={
                                            item.type === 'dashboard'
                                                ? `dashboard:${item.dashboard.id}`
                                                : `file:${item.file.id}`
                                        }
                                        data-index={index}
                                        onMouseMove={() =>
                                            handleFileMouseMove(index)
                                        }
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() =>
                                            handleReferenceSelect(item)
                                        }
                                        className={menuItemClassName}
                                    >
                                        {item.type === 'dashboard' ? (
                                            <span className="flex-1 min-w-0 truncate">
                                                {item.dashboard.title}
                                            </span>
                                        ) : (
                                            <>
                                                <OfficeFileIcon
                                                    extension={getFileExtension(
                                                        item.file.filename
                                                    )}
                                                    size={20}
                                                />
                                                <span className="flex-1 min-w-0 truncate">
                                                    {item.file.filename}
                                                </span>
                                                <span className="pl-2 font-normal text-(length:--fs-caption) text-muted-foreground leading-(--lh-caption) shrink-0">
                                                    {item.file.generated
                                                        ? 'Generated'
                                                        : 'Uploaded'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : isLoadingSessionFiles || isLoadingDashboards ? (
                            <div aria-busy className="p-1.5">
                                {['w-40', 'w-28'].map((nameWidth) => (
                                    <div
                                        key={nameWidth}
                                        className="flex items-center gap-2 px-2.5 h-9"
                                    >
                                        <Skeleton className="rounded-sm size-5" />
                                        <Skeleton className={`h-3 ${nameWidth}`} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="px-4 py-3 text-(length:--fs-subtitle) text-muted-foreground leading-(--lh-subtitle)">
                                {dashboardOnly
                                    ? fileQuery
                                        ? `No dashboards match “${fileQuery}”`
                                        : 'No dashboards yet'
                                    : fileQuery
                                      ? `No files match “${fileQuery}”`
                                      : 'No files yet'}
                            </p>
                        )}
                        {previewDashboardId && (
                            <div className="hidden md:block top-0 left-full absolute pl-2">
                                <DashboardPreview
                                    key={previewDashboardId}
                                    dashboardId={previewDashboardId}
                                    width={360}
                                />
                            </div>
                        )}
                    </PopoverContent>
                </Popover>
            </div>
        );
    }
);

export default Input;
